import { defineStore } from 'pinia'
import Decimal from 'decimal.js-light'
import { clamp, get } from '@vueuse/core'
import { useFutureStore } from './future'
import { orderbook } from '~/apis/socket'

export const usePanelStore = defineStore('panel', {
  state: () => ({
    pe: 1, // 1开仓 2平仓
    type: 'LIMIT',
    unitType: 'USDT', // 下单单位 LOT、COIN、USDT
    price: '',
    amount: '',
    sliderValue: 0,
    stop: { // 条件委托
      triggerPrice: '',
      triggerPriceType: 'LAST', // 2-最新价、4-标记价
      type: 'LIMIT', // 0-限价、1-市价
    },
  }),
  getters: {
    isClosePosotion() {
      return this.pe === 2
    },
    triggerDirection() {
      const { c, mark } = useFutureStore().overview
      const types = {
        LAST: c,
        MARK: mark,
      }
      return +this.stop.triggerPrice > +types[this.stop.triggerPriceType] ? 1 : -1
    },
    getPriceBySide() {
      const future = useFutureStore()
      const { mark = 0 } = future.overview
      const { a1 = 0, b1 = 0 } = future.bbo
      return ({ price = this.price, side }) => {
        if (this.isMarket)
          return Math.max(mark, side === 1 ? a1 : b1)
        return +price || Math.max(mark, side === 1 ? a1 : b1, +price) || 0 // 用户未输入价格，走市价
      }
    },
    // 张转化计价币（USDT）
    lots2Quote() {
      const future = useFutureStore()
      return (lots, side) => mul(lots, future.size).mul(this.getPriceBySide({ side }))
    },
    // 名义价值 ==> 张  (名义价值/价格/面值)
    quote2Lots() {
      const future = useFutureStore()
      return (side, size = future.size) => {
        const olc = mul(this.getPriceBySide({ side }), size)
        return fixRaw(Decimal(this.amount || 0).div(olc), 0)
      }
    },
    // 张转化基础币（BTC）
    lots2Base() {
      const future = useFutureStore()
      return lots => mul(lots, future.size)
    },
    // 张 ==> 对应单位
    lots2Result() {
      const future = useFutureStore()
      return (lots, side) => {
        if (future.isLotQuote) {
          return this.lots2Quote(lots, side)
        }
        else if (future.isLotBase) {
          return this.lots2Base(lots)
        }
        else if (future.isLotCost) {
          return mul(lots, future.getOneLotCost({ price: this.getPriceBySide({ side }) }))
        }
        return lots
      }
    },
    maxOpen() {
      const future = useFutureStore()
      return ({ side = 1, price }) => {
        price ??= this.getPriceBySide({ side })
        if (price) {
          const cost = future.getOneLotCost({ price })
          const trade = future.getMarginBalanceByCode(future.baseM)
          // 余额最大可开： tradeLots = 可用保证金 / 合约成本  ==> 取整
          const tradeLots = fixRaw(div(trade, cost).mul(side), 0)
          const orderLots = future.getOrderLotsByCode()
          const positionLots = future.getPositionLotsByCode()
          // 已存在仓位数量 existedQty = 未成交挂单数量 + 持仓数量
          const existedLots = add(orderLots[side], future.isHedgeMode ? positionLots[side] : positionLots.total)
          const marginRiskLimitSize = future.getRiskLimitByLeverage()?.maxSize || 0
          const clampLots = clamp(add(tradeLots, existedLots), marginRiskLimitSize * -1, marginRiskLimitSize)
          let maxOpen = Decimal(clampLots).minus(existedLots).abs()

          /**
           * 处理持仓与开仓方向相反的情况 （因为方向相反类似平仓操作，平仓不需要保证金）
           * 持仓数量 < 0（方向相反） && （持仓数量绝对值 >= 委托单未成交数量绝对值) && 单向持仓
           */
          if (
            Decimal(positionLots.total).mul(side).lt(0)
            && Decimal(positionLots.total).abs().gte(orderLots[side])
            && !future.isHedgeMode
          ) {
            maxOpen = Math.max(maxOpen, existedLots.abs())
          }
          // 和单笔下单限制做比较
          maxOpen = Math.min(future.curInfo?.maxOrderSize || 0, maxOpen)
          return this.isClosePosotion ? future.isGrants ? future.getLotsByCode(future.code, 'bonus')[side * -1] : future.getLotsByCode()[side * -1] : (maxOpen || 0)
        }
        return 0
      }
    },
    getPanelLots() {
      const future = useFutureStore()
      return (side = 1) => {
        if (this.sliderValue) {
          const maxLots = this.maxOpen({ side })
          return Decimal(this.sliderValue).div(100).mul(maxLots).toFixed(0, Decimal.ROUND_DOWN)
        }
        if (future.isLot)
          return this.amount
        if (future.isLotBase)
          return +Decimal(this.amount || 0).div(future.size).toFixed(0)
        if (future.isLotCost) {
          return div(this.amount || 0, future.getOneLotCost({ price: this.getPriceBySide({ side }) })).toFixed(0)
        }
        else {
          return this.quote2Lots(side)
        }
      }
    },
    isTrigger() {
      return this.type === 'TRIGGER'
    },
    isLimit() {
      return this.type === 'LIMIT'
    },
    isMarket() {
      return this.type === 'MARKET'
    },
    isOrderLimit() {
      return this.type === 'LIMIT' || (this.type === 'TRIGGER' && this.stop.type === 'LIMIT')
    },
    isOrderMarket() {
      return this.type === 'MARKET' || (this.type === 'TRIGGER' && this.stop.type === 'MARKET')
    },
    isStopLimit() {
      return this.type === 'TRIGGER' && this.stop.type === 'LIMIT'
    },
    isStopMarket() {
      return this.type === 'TRIGGER' && this.stop.type === 'MARKET'
    },
  },
})
